import BookingActions from "./BookingActions";
import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useContentSetting } from "../../../Hooks/ContentSetting";
import {
  surface, border, textPrimary, textSecondary, textMuted, font, shadow,
  accent, success, primaryButtonSx, secondaryButtonSx, inputSx, thSx, statTileSx,
  formatSubjectName,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import {
  get_school_bookings_api, get_school_tutoring_summary_api,
  mark_school_onboarding_seen_api,
  get_school_tutors_config_api,
  get_pending_access_requests_api, respond_marketplace_access_api,
  list_school_instructors_api, update_instructor_rate_api,
  admin_refund_booking_api,
} from "../../../DAL/SchoolPortal/Tutors";
import TutorsOnboardingModal from "../../../components/TutorsOnboardingModal";
import { PaymentFlowIllustration, ChannelSplitIllustration, RollupIllustration } from "../../../components/tutorsOnboardingIllustrations";
import { get_school_run_rate_api } from "../../../DAL/SchoolPortal/SchoolPortal";
import { currencySymbol, localeFor, regionOf } from "../../../utils/region";

const card = { backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "14px", boxShadow: shadow, p: 3, mb: 3 };
const sectionTitle = { fontSize: "15px", fontWeight: 700, color: textPrimary, fontFamily: font, mb: 2 };

// A function of the actual fee, not a constant. The middle slide asserted
// "XAir takes a 15% fee either way" — false for a school on a platform
// contract, which is the US model, so a US owner's first login contradicted
// the pitch they signed.
const adminOnboardingSlides = (fee) => [
  {
    illustration: <PaymentFlowIllustration />,
    title: "XAir Tutors is on for your school",
    description: "Your instructors set up their own Stripe payouts and availability — students pay through XAir, and money lands with them (or your school) automatically.",
  },
  {
    illustration: <ChannelSplitIllustration />,
    title: "In-house by default",
    description: `Only your own students can book your own instructors${fee.applies ? ` — ${fee.label} either way` : " — you are on a platform contract, so there is no per-booking fee"}. Turn on the open marketplace in Settings to also let students cross schools${fee.applies ? `, at the same ${fee.label} either direction` : ""}.`,
  },
  {
    illustration: <RollupIllustration />,
    title: "See exactly what's happening",
    description: "Every booking, your total revenue, and how much each instructor is earning — plus any instructor requests waiting on you — all right here in the Tutors section.",
  },
];

const STATUS_COLORS = {
  pending_instructor_approval: "#d97706",
  confirmed: "#2563eb",
  completed: success,
  declined: textMuted,
  cancelled: textMuted,
};

// Must match PAYOUT_HOLD_HOURS in web-backend/controllers/tutors/lessonPayout.js.
const PAYOUT_HOLD_HOURS = 72;

function payoutLabel(b, schoolRegion) {
  if (b.payment_status !== "paid") return "—"; // declined/cancelled — nothing was collected
  if (b.payout_status === "paid") {
    return `Paid ${b.payout_paid_at ? new Date(b.payout_paid_at).toLocaleDateString(localeFor(schoolRegion), { day: "numeric", month: "short" }) : ""}`;
  }
  const expected = new Date(new Date(b.end_time).getTime() + PAYOUT_HOLD_HOURS * 60 * 60 * 1000);
  return `Due ${expected.toLocaleDateString(localeFor(schoolRegion), { day: "numeric", month: "short" })}`;
}

function BookingsTable({ schoolRegion = "UK", rows, showSchoolColumn, showPayoutColumn, canRefund, onRefund, refundingId, onChanged = () => {},}) {
  if (rows.length === 0) {
    return <Typography sx={{ fontFamily: font, fontSize: "13px", color: textMuted }}>No bookings yet.</Typography>;
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={thSx}>Instructor</TableCell>
          {showSchoolColumn && <TableCell sx={thSx}>School</TableCell>}
          <TableCell sx={thSx}>Subject</TableCell>
          <TableCell sx={thSx}>When</TableCell>
          <TableCell sx={thSx}>Price</TableCell>
          <TableCell sx={thSx}>Status</TableCell>
          {showPayoutColumn && <TableCell sx={thSx}>Payout</TableCell>}
          {canRefund && <TableCell sx={thSx} />}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((b) => {
          const refundable = ["confirmed", "completed"].includes(b.status) && b.payment_status === "paid";
          return (
            <TableRow key={b._id}>
              <TableCell sx={{ fontFamily: font, fontSize: "13px", border: "none" }}>
                {b.instructor ? `${b.instructor.first_name} ${b.instructor.last_name}` : "—"}
              </TableCell>
              {showSchoolColumn && (
                <TableCell sx={{ fontFamily: font, fontSize: "13px", border: "none" }}>{b.flightSchool?.name || "—"}</TableCell>
              )}
              <TableCell sx={{ fontFamily: font, fontSize: "13px", border: "none" }}>{b.subject ? formatSubjectName(b.subject) : "—"}</TableCell>
              <TableCell sx={{ fontFamily: font, fontSize: "13px", border: "none" }}>
                {new Date(b.start_time).toLocaleString(localeFor(schoolRegion), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </TableCell>
              <TableCell sx={{ fontFamily: font, fontSize: "13px", border: "none" }}>{currencySymbol(schoolRegion)}{b.price}</TableCell>
              <TableCell sx={{ border: "none" }}>
                <Chip size="small" label={b.status.replace(/_/g, " ")} sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: STATUS_COLORS[b.status] || textMuted, backgroundColor: `${STATUS_COLORS[b.status] || textMuted}1a` }} />
              </TableCell>
              {showPayoutColumn && (
                <TableCell sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, border: "none" }}>{payoutLabel(b, schoolRegion)}</TableCell>
              )}
              {canRefund && (
                <TableCell sx={{ border: "none" }}>
                  {refundable && (
                    <Button
                      size="small"
                      onClick={() => onRefund(b._id)}
                      disabled={refundingId === b._id}
                      sx={{ color: textMuted, textTransform: "none", fontFamily: font, fontSize: "11.5px", p: 0, minWidth: 0, "&:hover": { color: "#c01e2e", backgroundColor: "transparent" } }}
                    >
                      {refundingId === b._id ? "Refunding…" : "Refund"}
                    </Button>
                  )}
                  {/* Refund was the ONLY thing an owner could do to a booking.
                      With a CFI off sick the front desk could refund a session
                      that had already happened and not cancel one that had not
                      — and that phone call comes to the front desk. Cancel and
                      move are now here too, scoped server-side to this school. */}
                  <BookingActions booking={b} canTeach={false} isManager onDone={onChanged} />
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default function AdminTutorsView() {
  const { enqueueSnackbar } = useSnackbar();
  const { userInfo } = useContentSetting();
  // Receptionist (operations) lands here too (anyone who isn't "instructor"),
  // but only Owner/Manager can change settings or approve requests — this
  // view still shows them the read-only booking history/rollup below.
  const canManage = ["primary_admin", "staff_admin"].includes(userInfo?.role);

  const [loading, setLoading] = useState(true);
  // null = all time. A cumulative total is the only thing this page could show
  // before, which makes "what did we bill in March" an unanswerable question on
  // the screen where a contract price is argued.
  const [period, setPeriod] = useState(null);
  const [runRate, setRunRate] = useState(null);
  const [bookings, setBookings] = useState({ inbound: [], outbound: [] });
  const [summary, setSummary] = useState({ instructors: [], totalRevenue: 0, totalLessons: 0 });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [config, setConfig] = useState(null);

  /**
   * What XAir actually charges this school (task 11.33).
   *
   * The copy hardcoded "XAir takes a 15% fee either way". A school on a
   * platform contract pays a flat annual figure and NO per-booking commission
   * (task 2.1), which is exactly the US model — so a US owner's first login
   * contradicted the pitch they signed. The number was also hardcoded rather
   * than read, so a school on a negotiated rate saw someone else's.
   */
  const fee = (() => {
    const onContract = Boolean(config?.platformContract?.active);
    const rate = config?.marketplaceCommissionRate;
    if (onContract) return { applies: false, label: "No per-booking fee", tile: "Included in your contract" };
    const pct = typeof rate === "number" ? Math.round(rate * 100) : null;
    return {
      applies: true,
      label: pct === null ? "XAir's platform fee" : `XAir takes a ${pct}% fee`,
      tile: "XAir's platform fee",
    };
  })();
  const schoolRegion = regionOf(config);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [schoolInstructors, setSchoolInstructors] = useState([]);
  const [rateDrafts, setRateDrafts] = useState({});
  const [refundingId, setRefundingId] = useState(null);

  useEffect(() => {
    loadAll();
    const seen = localStorage.getItem("tutors_admin_onboarding_seen");
    if (!seen) setShowOnboarding(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const loadAll = async () => {
    setLoading(true);
    const calls = [
      get_school_bookings_api(),
      get_school_tutoring_summary_api(period ? { from: period.from, to: period.to } : {}),
      get_school_tutors_config_api(),
    ];
    if (canManage) {
      calls.push(get_pending_access_requests_api(), list_school_instructors_api());
    }
    const [bookingsRes, summaryRes, configRes, requestsRes, instructorsRes] = await Promise.all(calls);
    if (bookingsRes.code === 200) setBookings(bookingsRes.data);
    if (summaryRes.code === 200) setSummary(summaryRes.data);
    if (configRes.code === 200) setConfig(configRes.data);
    if (requestsRes?.code === 200) setPendingRequests(requestsRes.data);
    if (instructorsRes?.code === 200) setSchoolInstructors(instructorsRes.data);
    setLoading(false);
  };

  useEffect(() => {
    // The month strip itself is period-independent — it IS the period picker.
    get_school_run_rate_api(6).then((r) => { if (r.code === 200) setRunRate(r); }).catch(() => {});
  }, []);

  const dismissOnboarding = async () => {
    setShowOnboarding(false);
    localStorage.setItem("tutors_admin_onboarding_seen", "true");
    await mark_school_onboarding_seen_api();
  };

  const handleRequestDecision = async (instructorId, decision) => {
    const res = await respond_marketplace_access_api(instructorId, decision);
    if (res.code === 200) {
      setPendingRequests((prev) => prev.filter((r) => r._id !== instructorId));
      enqueueSnackbar(decision === "approve" ? "Access approved" : "Request denied", { variant: "success" });
    } else {
      enqueueSnackbar(res.message || "Couldn't update this request", { variant: "error" });
    }
  };

  const saveInstructorRate = async (instructorId) => {
    const value = parseFloat(rateDrafts[instructorId]);
    if (Number.isNaN(value) || value < 0) return;
    const res = await update_instructor_rate_api(instructorId, value);
    if (res.code === 200) {
      setSchoolInstructors((prev) => prev.map((i) => (i._id === instructorId ? { ...i, tutoring: { ...i.tutoring, hourlyRate: value } } : i)));
      enqueueSnackbar("Rate updated", { variant: "success" });
    } else {
      enqueueSnackbar(res.message || "Couldn't update rate", { variant: "error" });
    }
  };

  const handleRefund = async (bookingId) => {
    if (!window.confirm("Refund this lesson? This charges the full amount back to the student.")) return;
    setRefundingId(bookingId);
    const res = await admin_refund_booking_api(bookingId);
    setRefundingId(null);
    if (res.code === 200) {
      setBookings((prev) => ({
        ...prev,
        inbound: prev.inbound.map((b) => (b._id === bookingId ? res.data.booking : b)),
      }));
      const clawback = res.data.clawbackStatus;
      if (clawback === "failed") {
        enqueueSnackbar("Refunded, but clawing back the payout failed — follow up manually with the instructor/school", { variant: "warning" });
      } else {
        enqueueSnackbar("Refunded", { variant: "success" });
      }
    } else {
      enqueueSnackbar(res.message || "Couldn't refund this booking", { variant: "error" });
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: accent }} /></Box>;
  }

  return (
    <>
      <TutorsOnboardingModal open={showOnboarding} onClose={dismissOnboarding} slides={adminOnboardingSlides(fee)} />

      {!canManage && (
        <Box sx={{ ...card, backgroundColor: surface }}>
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary }}>
            You have Receptionist access — you can view bookings and activity below. Ask an Owner or Manager for changes to Tutors settings or instructor requests.
          </Typography>
        </Box>
      )}

      {canManage && pendingRequests.length > 0 && (
        <Box sx={card}>
          <Typography sx={sectionTitle}>
            Requests waiting on you <Chip size="small" label={pendingRequests.length} sx={{ ml: 1, backgroundColor: "rgba(217,119,6,0.15)", color: "#d97706", fontWeight: 700 }} />
          </Typography>
          {pendingRequests.map((r) => (
            <Box key={r._id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.25, borderBottom: `1px solid ${border}` }}>
              <Box>
                <Typography sx={{ fontFamily: font, fontSize: "14px", fontWeight: 600, color: textPrimary }}>{r.first_name} {r.last_name}</Typography>
                <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary }}>wants to become visible on the open marketplace</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" onClick={() => handleRequestDecision(r._id, "deny")} sx={{ ...secondaryButtonSx, py: 0.75, px: 2 }}>Deny</Button>
                <Button size="small" onClick={() => handleRequestDecision(r._id, "approve")} sx={{ ...primaryButtonSx, py: 0.75, px: 2, minHeight: "auto" }}>Approve</Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {canManage && config?.rateMode === "admin_set" && schoolInstructors.length > 0 && (
        <Box sx={card}>
          <Typography sx={sectionTitle}>Instructor rates</Typography>
          <Typography sx={{ fontSize: "12.5px", color: textSecondary, fontFamily: font, mb: 2 }}>
            Your school's rate policy is set to "you set each instructor's rate" — change that in Settings if you'd rather instructors set their own.
          </Typography>
          {schoolInstructors.map((instructor) => (
            <Box key={instructor._id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, flex: 1 }}>{instructor.first_name} {instructor.last_name}</Typography>
              <TextField
                size="small"
                type="number"
                placeholder={String(config.defaultHourlyRate ?? 50)}
                value={rateDrafts[instructor._id] ?? instructor.tutoring?.hourlyRate ?? ""}
                onChange={(e) => setRateDrafts((prev) => ({ ...prev, [instructor._id]: e.target.value }))}
                sx={{ ...inputSx, width: 110 }}
              />
              <Button size="small" onClick={() => saveInstructorRate(instructor._id)} sx={{ ...secondaryButtonSx, py: 0.75, px: 2 }}>Save</Button>
            </Box>
          ))}
        </Box>
      )}

      {/* The period picker. Every month is a real figure from the run-rate
          report, so choosing one is also reading the trend. */}
      {runRate?.series?.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.75, mb: 2, flexWrap: "wrap", alignItems: "stretch" }}>
          <Box
            onClick={() => setPeriod(null)}
            sx={{
              px: 1.25, py: 0.75, borderRadius: "8px", cursor: "pointer", fontFamily: font,
              border: `1px solid ${period === null ? textPrimary : border}`,
              backgroundColor: period === null ? textPrimary : surface,
              color: period === null ? "#fff" : textSecondary,
              fontSize: "12px", fontWeight: 700, display: "flex", alignItems: "center",
            }}
          >
            All time
          </Box>
          {runRate.series.map((m) => {
            const from = `${m.month}-01`;
            const on = period?.from === from;
            const [y, mm] = m.month.split("-").map(Number);
            const to = `${mm === 12 ? y + 1 : y}-${String(mm === 12 ? 1 : mm + 1).padStart(2, "0")}-01`;
            return (
              <Box
                key={m.month}
                onClick={() => setPeriod({ from, to })}
                sx={{
                  px: 1.25, py: 0.6, borderRadius: "8px", cursor: "pointer", fontFamily: font, minWidth: 76,
                  border: `1px solid ${on ? textPrimary : border}`,
                  backgroundColor: on ? textPrimary : surface,
                  color: on ? "#fff" : textPrimary,
                }}
              >
                <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: on ? "rgba(255,255,255,0.75)" : textSecondary }}>
                  {new Date(y, mm - 1, 1).toLocaleString(localeFor(schoolRegion), { month: "short" })}
                </Typography>
                <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "inherit" }}>
                  {currencySymbol(schoolRegion)}{Math.round(m.revenue || 0)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {runRate?.reviewDue && (
        <Box sx={{ mb: 2, p: 1.25, borderRadius: "8px", border: `1px solid ${accent}33`, backgroundColor: `${accent}0a` }}>
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.45 }}>
            You are {runRate.monthsLive} months in. Your run-rate review is due — the numbers above are what it will be based on.
          </Typography>
        </Box>
      )}

      {period && (
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mb: 1 }}>
          Showing lessons taught in this month only. Bookings below are not filtered.
        </Typography>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr" }, gap: 2, mb: 3 }}>
        <Box sx={statTileSx}>
          <Typography sx={{ fontSize: "20px", fontWeight: 800, color: textPrimary, fontFamily: font }}>{currencySymbol(schoolRegion)}{(summary.totalRevenue || 0).toFixed(2)}</Typography>
          <Typography sx={{ fontSize: "12px", color: textSecondary, fontFamily: font, mt: 0.25 }}>Total revenue</Typography>
        </Box>
        <Box sx={statTileSx}>
          <Typography sx={{ fontSize: "20px", fontWeight: 800, color: textPrimary, fontFamily: font }}>{summary.totalLessons || 0}</Typography>
          <Typography sx={{ fontSize: "12px", color: textSecondary, fontFamily: font, mt: 0.25 }}>Lessons completed</Typography>
        </Box>
        <Box sx={statTileSx}>
          <Typography sx={{ fontSize: "20px", fontWeight: 800, color: textPrimary, fontFamily: font }}>{currencySymbol(schoolRegion)}{(summary.totalCommission || 0).toFixed(2)}</Typography>
          <Typography sx={{ fontSize: "12px", color: textSecondary, fontFamily: font, mt: 0.25 }}>{fee.tile}</Typography>
        </Box>
        <Box sx={statTileSx}>
          <Typography sx={{ fontSize: "20px", fontWeight: 800, color: textPrimary, fontFamily: font }}>{currencySymbol(schoolRegion)}{(summary.netPaidOut || 0).toFixed(2)}</Typography>
          <Typography sx={{ fontSize: "12px", color: textSecondary, fontFamily: font, mt: 0.25 }}>Paid to instructors/school</Typography>
        </Box>
      </Box>

      <Box sx={card}>
        <Typography sx={sectionTitle}>Revenue per instructor</Typography>
        {summary.instructors.length === 0 ? (
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: textMuted }}>No completed lessons yet.</Typography>
        ) : (
          summary.instructors.map((i) => (
            <Box key={i.instructorId} sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: `1px solid ${border}` }}>
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary }}>{i.name}</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary }}>
                {i.lessonsCount} lessons · {currencySymbol(schoolRegion)}{i.revenue.toFixed(2)} <span style={{ color: textMuted }}>({currencySymbol(schoolRegion)}{i.commission.toFixed(2)} fee)</span>
              </Typography>
            </Box>
          ))
        )}
      </Box>

      <Box sx={card}>
        <Typography sx={sectionTitle}>Bookings for your instructors</Typography>
        <BookingsTable schoolRegion={schoolRegion}           rows={bookings.inbound} onChanged={loadAll}
          showSchoolColumn={false}
          showPayoutColumn
          canRefund={canManage}
          onRefund={handleRefund}
          refundingId={refundingId}
        />
      </Box>

      {bookings.outbound.length > 0 && (
        <Box sx={card}>
          <Typography sx={sectionTitle}>Your students' bookings at other schools</Typography>
          <BookingsTable schoolRegion={schoolRegion} rows={bookings.outbound} showSchoolColumn onChanged={loadAll} />
        </Box>
      )}
    </>
  );
}
